import React from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  FieldLabel,
  Row,
  RowWrapper,
  Section,
  SectionTitle,
  UserFormProps,
  getInputField,
  FieldContainer,
} from '../../../components/AdminFormComponents';
import { Tooltip } from 'react-covideo-common';
import { MdInfo } from 'react-icons/md';
import { theme } from 'lib/style';
import CountryCodePicker from 'lib/components/country-code-picker/CountryCodePicker';
import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';
import { Field, FieldProps } from 'formik';
import { TextInput } from 'lib/components';

export type CountryCode = ReturnType<typeof getCountries>[number];

export const parseLocalNumber = (phone: string): string => {
  if (!phone.startsWith('+')) {
    return phone.trim();
  }
  return phone.replace(/^\+\d+\s?/, '').trim();
};

export const parseCountryFromPhone = (phone: string): string => {
  if (!phone) return 'US';

  const phoneNumber = parsePhoneNumberFromString(phone);

  return phoneNumber?.country || 'US';
};

export const combinePhone = (
  country: CountryCode,
  localNumber: string
): string => {
  if (!localNumber.trim()) {
    return '';
  }

  const callingCode = '+' + getCountryCallingCode(country);
  return `${callingCode} ${localNumber.trim()}`;
};

export const ContactSection = (props: UserFormProps) => {
  const { isSubmitting, isNewUser, hasEmail2Field, values, setFieldValue } =
    props;

  const [phone1Country, setPhone1Country] = React.useState(
    parseCountryFromPhone(values.phone1 || '')
  );
  const [phone1Local, setPhone1Local] = React.useState(
    parseLocalNumber(values.phone1 || '')
  );
  const [phone2Country, setPhone2Country] = React.useState(
    parseCountryFromPhone(values.phone2 || '')
  );
  const [phone2Local, setPhone2Local] = React.useState(
    parseLocalNumber(values.phone2 || '')
  );
  const [cellPhoneCountry, setCellPhoneCountry] = React.useState(
    parseCountryFromPhone(values.cellPhone || '')
  );
  const [cellPhoneLocal, setCellPhoneLocal] = React.useState(
    parseLocalNumber(values.cellPhone || '')
  );

  const handleCountryChange = (
    phoneField: 'phone1' | 'phone2' | 'cellPhone',
    newCountry: string
  ) => {
    switch (phoneField) {
      case 'phone1':
        setPhone1Country(newCountry);
        setFieldValue(
          'phone1',
          combinePhone(newCountry as CountryCode, phone1Local)
        );
        setPhone2Country(newCountry);
        setFieldValue(
          'phone2',
          combinePhone(newCountry as CountryCode, phone2Local)
        );
        setCellPhoneCountry(newCountry);
        setFieldValue(
          'cellPhone',
          combinePhone(newCountry as CountryCode, cellPhoneLocal)
        );
        break;
      case 'phone2':
        setPhone2Country(newCountry);
        setFieldValue(
          'phone2',
          combinePhone(newCountry as CountryCode, phone2Local)
        );
        break;
      case 'cellPhone':
        setCellPhoneCountry(newCountry);
        setFieldValue(
          'cellPhone',
          combinePhone(newCountry as CountryCode, cellPhoneLocal)
        );
        break;
      default:
        break;
    }
  };

  const handleLocalChange = (
    phoneField: 'phone1' | 'phone2' | 'cellPhone',
    newLocal: string
  ) => {
    const valid = /^[0-9\s\-().]*$/.test(newLocal);
    if (!valid) {
      return;
    }
    switch (phoneField) {
      case 'phone1':
        setPhone1Local(newLocal);
        setFieldValue(
          'phone1',
          combinePhone(phone1Country as CountryCode, newLocal)
        );
        break;
      case 'phone2':
        setPhone2Local(newLocal);
        setFieldValue(
          'phone2',
          combinePhone(phone2Country as CountryCode, newLocal)
        );
        break;
      case 'cellPhone':
        setCellPhoneLocal(newLocal);
        setFieldValue(
          'cellPhone',
          combinePhone(cellPhoneCountry as CountryCode, newLocal)
        );
        break;
      default:
        break;
    }
  };

  return (
    <Section id={'contactInfo'}>
      <SectionTitle>Contact</SectionTitle>
      <RowWrapper>
        {!isNewUser && (
          <Row>
            <FieldLabel>Email</FieldLabel>
            {getInputField('email', true, false, isSubmitting, 'email')}
          </Row>
        )}
        {!isNewUser && hasEmail2Field && (
          <Row>
            <FieldLabel>Email 2</FieldLabel>
            {getInputField('email2', false, false, isSubmitting, 'email')}
          </Row>
        )}
        <Row>
          <FieldLabel>Phone 1</FieldLabel>
          <Field type={'number'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={phone1Country}
                    setExtension={newCountry =>
                      handleCountryChange('phone1', newCountry)
                    }
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={phone1Local}
                    disabled={isSubmitting}
                    onChange={e => handleLocalChange('phone1', e.target.value)}
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
        <Row>
          <FieldLabel>Phone 2</FieldLabel>
          <Field type={'number'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={phone2Country}
                    setExtension={newCountry =>
                      handleCountryChange('phone2', newCountry)
                    }
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={phone2Local}
                    disabled={isSubmitting}
                    onChange={e => handleLocalChange('phone2', e.target.value)}
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
        <Row>
          <FieldLabel>
            Cell Phone
            <Tooltip
              popup={
                'This is useful for delivering important onboarding content to each user.'
              }
              position='right'
              extendStyles={{
                popup: {
                  width: '310px',
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: theme.palette.neutral100,
                  padding: '8px 12px',
                  fontWeight: '400',
                },
              }}
            >
              <MdInfo
                style={{
                  top: '2px',
                }}
              />
            </Tooltip>
          </FieldLabel>
          <Field type={'number'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={cellPhoneCountry}
                    setExtension={newCountry =>
                      handleCountryChange('cellPhone', newCountry)
                    }
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={cellPhoneLocal}
                    disabled={isSubmitting}
                    onChange={e =>
                      handleLocalChange('cellPhone', e.target.value)
                    }
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
      </RowWrapper>
    </Section>
  );
};
